import { useState, useEffect, useCallback, useRef } from 'react'
import { useSocket } from '../contexts/SocketContext'
import { apiGet, getErrorMessage } from '../utils/api'
import { useAudioAlerts } from './useAudioAlerts'

export interface QueueChat {
  id: string
  customerId: string
  customerName: string
  customerEmail: string
  plan: 'free' | 'pro' | 'enterprise'
  priority: 'low' | 'medium' | 'high'
  tags: string[]
  status: 'queued' | 'open' | 'closed' | 'missed' | 'auto_closed' | 'archived'
  /** AI First Responder state (read-only badge). null = ordinary human chat. */
  aiState?: 'suppressed' | 'active' | 'escalated' | 'closed_by_ai' | 'abandoned' | null
  assignedAgentId?: string
  assignedAgentName?: string
  createdAt: string
  lastMessageAt: string
  waitTime: number
  messageCount: number
  firstCustomerMessage?: string
  latestMessage?: { content: string; sender: string }
  channel?: 'widget' | 'whatsapp'
  visitorName?: string
  companyName?: string
  companyLogoUrl?: string
  companyCountry?: string
  accountAlias?: string
}

interface UseQueueReturn {
  chats: QueueChat[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  filterTags: string[]
  setFilterTags: (tags: string[]) => void
  filterStatus: string
  setFilterStatus: (status: string) => void
}

/** Map raw snake_case chat data to camelCase QueueChat */
export function mapRawChat(c: any): QueueChat {
  return {
    id: c.id || c.chat_id,
    customerId: c.customer_id || c.customerId || '',
    customerName: c.customer_name || c.customerName || 'Unknown',
    customerEmail: c.customer_email || c.customerEmail || '',
    plan: c.customer_plan_tier || c.plan || 'free',
    priority: c.priority || 'medium',
    tags: Array.isArray(c.tags) ? c.tags : (typeof c.tags === 'string' && c.tags ? JSON.parse(c.tags) : []),
    status: c.status || 'queued',
    assignedAgentId: c.agent_id || c.assignedAgentId,
    assignedAgentName: c.agent_name || c.assignedAgentName,
    createdAt: c.created_at || c.createdAt || new Date().toISOString(),
    lastMessageAt: c.last_message_at || c.lastMessageAt || c.created_at || c.createdAt || new Date().toISOString(),
    waitTime: c.wait_time_seconds || c.waitTime || 0,
    messageCount: c.message_count || c.messageCount || 0,
    firstCustomerMessage: c.first_customer_message || c.firstCustomerMessage || c.last_message_preview || c.lastMessagePreview || undefined,
    latestMessage: c.latest_message
      ? { content: c.latest_message.body || c.latest_message.content || '', sender: c.latest_message.sender_type || c.latest_message.sender || 'customer' }
      : (c.latestMessage || undefined),
    channel: c.channel || 'widget',
    aiState: c.ai_state || c.aiState || null,
    visitorName: c.visitor_name || c.visitorName || undefined,
    companyName: c.company_name || c.companyName || undefined,
    companyLogoUrl: c.company_logo_url || c.companyLogoUrl || undefined,
    companyCountry: c.company_country || c.companyCountry || undefined,
    accountAlias: c.account_alias || c.accountAlias || undefined,
  }
}

/** De-duplicate chats by id (keeps the first occurrence) */
function dedupeChats(chats: QueueChat[]): QueueChat[] {
  const seen = new Set<string>()
  return chats.filter((c) => {
    if (seen.has(c.id)) return false
    seen.add(c.id)
    return true
  })
}

/** Sort chats by most recent message first */
function sortChats(chats: QueueChat[]): QueueChat[] {
  return [...chats].sort((a, b) => {
    const aTime = new Date(a.lastMessageAt || a.createdAt).getTime()
    const bTime = new Date(b.lastMessageAt || b.createdAt).getTime()
    return bTime - aTime
  })
}

export const useQueue = (): UseQueueReturn => {
  const [chats, setChats] = useState<QueueChat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterTags, setFilterTags] = useState<string[]>([])
  const [filterStatus, setFilterStatus] = useState<string>('queued')
  const { socket } = useSocket()
  const { playQueueAlert, stopQueueAlert } = useAudioAlerts()

  // Use refs for values the socket handlers need, so the socket effect
  // doesn't re-register listeners every time filters change.
  const filterStatusRef = useRef(filterStatus)
  const filterTagsRef = useRef(filterTags)
  filterStatusRef.current = filterStatus
  filterTagsRef.current = filterTags

  // Ref for playQueueAlert so fetchQueue can call it without re-registering
  const playQueueAlertRef = useRef(playQueueAlert)
  playQueueAlertRef.current = playQueueAlert

  // Track whether we've done the initial page-load alert check
  const initialAlertDoneRef = useRef(false)

  // Debounce queue_update fetches to avoid 429 rate-limit errors.
  // Multiple events can fire in rapid succession; only the last one matters.
  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchQueue = useCallback(async (statusOverride?: string) => {
    const status = statusOverride ?? filterStatusRef.current
    const tags = filterTagsRef.current
    try {
      setLoading(true)
      const response = await apiGet<{ chats: any[]; total: number } | any[]>('/queue', {
        status: status || '',
        tags: tags.length > 0 ? tags.join(',') : ''
      })
      const rawChats = Array.isArray(response) ? response : ((response as any).queue || response.chats || [])
      const mapped = rawChats.map(mapRawChat)
      const dedupedSorted = sortChats(dedupeChats(mapped))
      setChats(dedupedSorted)
      setError(null)

      // Ensure alerting state is set for any queued/unassigned chats in the results.
      // This handles the case where the page-load alert fired before the QueueItems
      // were in the DOM, and also picks up new queued chats from filter switches.
      dedupedSorted
        .filter((c) => c.status === 'queued' && !c.assignedAgentId)
        .forEach((c) => playQueueAlertRef.current(c.id))
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch queue'))
    } finally {
      setLoading(false)
    }
  }, [])  // stable — uses refs for filter values

  /** Debounced version: coalesces rapid queue_update events into one fetch */
  const debouncedFetchQueue = useCallback(() => {
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current)
    fetchTimerRef.current = setTimeout(() => {
      fetchQueue()
    }, 300)
  }, [fetchQueue])

  // Fetch when filters change
  useEffect(() => {
    fetchQueue()
  }, [filterStatus, filterTags, fetchQueue])

  // On mount: one-time check for already-queued chats to set alerting state.
  // This runs regardless of the current filter, so the yellow pulse shows
  // even if the user is on the "Open" tab and there are queued chats waiting.
  useEffect(() => {
    if (initialAlertDoneRef.current) return
    initialAlertDoneRef.current = true
    ;(async () => {
      try {
        const resp = await apiGet<{ chats: any[]; total: number } | any[]>('/queue', { status: 'queued', tags: '' })
        const raw = Array.isArray(resp) ? resp : ((resp as any).queue || (resp as any).chats || [])
        const queued = raw.map(mapRawChat).filter((c: QueueChat) => !c.assignedAgentId)
        queued.forEach((c: QueueChat) => playQueueAlert(c.id))
      } catch {
        // non-critical — alerting just won't fire on page load
      }
    })()
  }, [playQueueAlert])

  // Socket event listeners — registered once per socket connection
  useEffect(() => {
    if (!socket) return

    const handleNewChat = (raw: any) => {
      const chat = mapRawChat(raw)
      setChats((prev) => {
        if (prev.some((c) => c.id === chat.id)) return prev
        return sortChats([chat, ...prev])
      })
      playQueueAlert(chat.id)
    }

    const handleChatAssigned = (data: { chatId: string; agentId: string; agentName: string }) => {
      setChats((prev) =>
        prev.map((chat) =>
          chat.id === data.chatId
            ? { ...chat, status: 'open' as const, assignedAgentId: data.agentId, assignedAgentName: data.agentName }
            : chat
        )
      )
      stopQueueAlert(data.chatId)
      debouncedFetchQueue()
    }

    const handleChatClosed = (chatId: string) => {
      // Clear both visual and audio state for this chat. If it later gets
      // reopened (same ID, which the server does when the customer reconnects),
      // the dashboard will alert afresh instead of staying silent.
      stopQueueAlert(chatId)
      debouncedFetchQueue()
    }

    const handleQueueUpdate = () => {
      debouncedFetchQueue()
    }

    // A customer sending another message on an EXISTING chat is signalled only as
    // `new_message` (to the queue room) — the server does NOT emit queue_update
    // for it. Without listening here, the inbox never reflected follow-up
    // messages from any chat the agent didn't have open, so they had to refresh
    // to see other customers' messages. Debounced so a burst = one refetch.
    const handleNewMessage = () => {
      debouncedFetchQueue()
    }

    socket.on('new_chat', handleNewChat)
    socket.on('chat_assigned', handleChatAssigned)
    socket.on('chat_closed', handleChatClosed)
    socket.on('queue_update', handleQueueUpdate)
    socket.on('new_message', handleNewMessage)

    return () => {
      socket.off('new_chat', handleNewChat)
      socket.off('chat_assigned', handleChatAssigned)
      socket.off('chat_closed', handleChatClosed)
      socket.off('queue_update', handleQueueUpdate)
      socket.off('new_message', handleNewMessage)
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current)
    }
  }, [socket, playQueueAlert, stopQueueAlert, debouncedFetchQueue])

  return {
    chats,
    loading,
    error,
    refresh: fetchQueue,
    filterTags,
    setFilterTags,
    filterStatus,
    setFilterStatus
  }
}
